import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { Eye, EyeOff, Check } from 'lucide-react';

const proofPoints = [
  'Monitor competitor ads across Meta, TikTok, and Google',
  'Track pricing and stock changes in real time',
  'Detect storefront tech signals and strategy shifts',
  'Get intelligent alerts delivered to your inbox',
  'Join 2,000+ Shopify merchants using RADAR',
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signIn({ email, password });
      try {
        const storedConnectId =
          typeof window !== 'undefined'
            ? window.localStorage.getItem('radar_shopify_connect_id')
            : null;
        if (storedConnectId) {
          navigate(`/shopify-connect?code=${storedConnectId}`);
        } else {
          navigate('/dashboard');
        }
      } catch {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout footer="none">
      <SEO title="Welcome back" description="Log in to your RADAR account." canonical="/login" noindex />
      <Section hero className="min-h-screen flex items-center">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-5xl mx-auto">
            {/* Left - Proof points (hidden on mobile) */}
            <div className="hidden lg:block">
              <AnimatedReveal>
                <Link to="/" className="flex items-center gap-2.5 mb-8">
                  <span className="text-lg font-display font-bold text-foreground">RADAR</span>
                </Link>
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">Competitive intelligence for operators who move fast</h2>
                <ul className="space-y-4">
                  {proofPoints.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </AnimatedReveal>
            </div>

            {/* Right - Login form */}
            <AnimatedReveal delay={0.15}>
              <GlassPanel className="p-8 sm:p-10" data-testid="auth-form">
                <h1 className="text-2xl font-display font-bold text-foreground mb-2">Welcome back</h1>
                <p className="text-sm text-muted-foreground mb-8">Log in to your RADAR account.</p>

                {error && (
                  <div className="mb-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                      data-testid="auth-email-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 pr-12 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                        data-testid="auth-password-input"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Link to="/reset-password" className="text-xs text-lime-400 hover:text-lime-300 transition-colors">
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_20px_rgba(204,255,108,0.15)] disabled:opacity-50"
                    data-testid="auth-submit-button"
                  >
                    {loading ? 'Signing in...' : 'Log in'}
                  </button>
                </form>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  Don&apos;t have an account?{' '}
                  <Link to="/register" className="text-lime-400 hover:text-lime-300 transition-colors">Start free trial</Link>
                </div>
              </GlassPanel>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
